import { yupResolver } from '@hookform/resolvers/yup';
import { useAcceptTerms } from '@sunrise-ui/api-client';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Button,
  ButtonVariants,
  InputError,
  TermsOfService,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { Checkbox } from 'react-native-paper';
import type { InferType } from 'yup';
import { boolean, object } from 'yup';
import { ModalStepHeader } from './ModalStepHeader';

const schema = object().shape({
  hasAcceptedTerms: boolean()
    .oneOf([true], 'You must accept our terms to continue')
    .required('You must accept our terms to continue'),
});

type FormValues = InferType<typeof schema>;

export function AcceptTermsModalScreen() {
  const styles = useDynamicStyles(createStyles);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { hasAcceptedTerms: false },
    resolver: yupResolver(schema),
  });
  const { mutate: acceptTerms } = useAcceptTerms();

  const onSubmit = () => {
    acceptTerms();
  };

  return (
    <View style={styles.container}>
      <ModalStepHeader headerText="Please Review Our Terms" />
      <Controller
        control={control}
        name="hasAcceptedTerms"
        render={({ field: { value, onChange } }) => (
          <View>
            <View style={styles.termsSection}>
              <Checkbox.Android
                status={value ? 'checked' : 'unchecked'}
                onPress={() => {
                  onChange(!value);
                }}
              />
              <TermsOfService />
            </View>
            <InputError fieldError={errors.hasAcceptedTerms} />
          </View>
        )}
      />
      <Button
        variant={ButtonVariants.PURPLE}
        size="medium"
        onPress={handleSubmit(onSubmit)}
        disabled={isSubmitting}
        loading={isSubmitting}
        style={styles.button}
      >
        Continue
      </Button>
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      alignItems: 'center',
      width: '100%',
      paddingVertical: 40,
      paddingHorizontal: 40,
    },
    termsSection: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 8,
      marginTop: isMobile ? 20 : 40,
    },
    button: {
      marginTop: isMobile ? 10 : 30,
    },
  });
